/**
 * pages/DashboardPage.js
 * 
 * Overview dashboard showing:
 * - Quick stats from all 3 modules
 * - Upcoming tasks
 * - Recent workout
 * - Sleep trend mini-chart
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workoutsAPI, sleepAPI, calendarAPI } from '../utils/api';
import { format, formatDistanceToNow } from 'date-fns';
import './DashboardPage.css';

// A single stat card
const StatCard = ({ icon, label, value, sub, color, to }) => (
  <Link to={to} className="stat-card card" style={{ '--card-accent': color }}>
    <div className="stat-icon">{icon}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </Link>
);

// Priority badge
const PriorityBadge = ({ priority }) => (
  <span className={`badge badge-${priority}`}>{priority}</span>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ workouts: null, sleep: null, tasks: null });
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [workoutStats, sleepAnalytics, taskStats, upcomingTasks] = await Promise.allSettled([
          workoutsAPI.stats(),
          sleepAPI.analytics(7),
          calendarAPI.stats(),
          calendarAPI.upcoming(3),
        ]);

        setStats({
          workouts: workoutStats.status === 'fulfilled' ? workoutStats.value : null,
          sleep: sleepAnalytics.status === 'fulfilled' ? sleepAnalytics.value : null,
          tasks: taskStats.status === 'fulfilled' ? taskStats.value : null,
        });

        if (upcomingTasks.status === 'fulfilled') {
          setUpcoming(upcomingTasks.value.tasks || []);
        }
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const avgSleepHours = stats.sleep?.averages?.avg_duration_mins
    ? (stats.sleep.averages.avg_duration_mins / 60).toFixed(1)
    : '—';

  return (
    <div className="dashboard animate-fadein">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>{greeting()}, {user?.name?.split(' ')[0]} 👋</h1>
          <p className="text-muted">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
          </p>
        </div>
        <div className="dashboard-actions">
          <Link to="/workouts" className="btn btn-primary btn-sm">+ Log Workout</Link>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid">
        <StatCard
          to="/workouts"
          icon="◈"
          label="Workouts This Week"
          value={loading ? '...' : stats.workouts?.this_week ?? '0'}
          sub={`${stats.workouts?.total_workouts ?? 0} total all time`}
          color="var(--accent)"
        />
        <StatCard
          to="/sleep"
          icon="◎"
          label="Avg Sleep (7 days)"
          value={loading ? '...' : `${avgSleepHours}h`}
          sub={stats.sleep?.averages?.avg_quality ? `${stats.sleep.averages.avg_quality}/5 quality` : ''}
          color="var(--purple)"
        />
        <StatCard
          to="/calendar"
          icon="◻"
          label="Tasks Due Soon"
          value={loading ? '...' : stats.tasks?.summary?.due_this_week ?? '0'}
          sub={`${stats.tasks?.summary?.overdue ?? 0} overdue`}
          color="var(--cyan)"
        />
        <StatCard
          to="/calendar"
          icon="✓"
          label="Completed Tasks"
          value={loading ? '...' : stats.tasks?.summary?.completed ?? '0'}
          sub={`${stats.tasks?.summary?.pending ?? 0} pending`}
          color="var(--green)"
        />
      </div>

      {/* Two column: upcoming tasks + quick add */}
      <div className="dashboard-grid">
        {/* Upcoming Tasks */}
        <div className="card">
          <div className="flex justify-between items-center" style={{ marginBottom: '1rem' }}>
            <h3>Upcoming Tasks</h3>
            <Link to="/calendar" className="btn btn-ghost btn-sm">View all →</Link>
          </div>

          {loading ? (
            <div className="text-muted">Loading...</div>
          ) : upcoming.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">◻</div>
              <p>No upcoming tasks</p>
              <Link to="/calendar" className="btn btn-secondary btn-sm">Add task</Link>
            </div>
          ) : (
            <div className="task-list">
              {upcoming.map(task => (
                <div key={task.id} className="task-item">
                  <div className="task-item-left">
                    <div className="task-dot" style={{ background: task.color || 'var(--accent)' }} />
                    <div>
                      <div className="task-title">{task.title}</div>
                      {task.due_date && (
                        <div className="task-due">
                          {formatDistanceToNow(new Date(task.due_date), { addSuffix: true })}
                        </div>
                      )}
                    </div>
                  </div>
                  <PriorityBadge priority={task.priority} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card quick-actions">
          <h3 style={{ marginBottom: '1rem' }}>Quick Actions</h3>
          <div className="quick-action-grid">
            <Link to="/workouts" className="quick-action">
              <div className="qa-icon" style={{ color: 'var(--accent)' }}>◈</div>
              <span>Log Workout</span>
            </Link>
            <Link to="/sleep" className="quick-action">
              <div className="qa-icon" style={{ color: 'var(--purple)' }}>◎</div>
              <span>Log Sleep</span>
            </Link>
            <Link to="/calendar" className="quick-action">
              <div className="qa-icon" style={{ color: 'var(--cyan)' }}>◻</div>
              <span>Add Task</span>
            </Link>
            <Link to="/sleep" className="quick-action">
              <div className="qa-icon" style={{ color: 'var(--yellow)' }}>≋</div>
              <span>Sleep Analytics</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Top exercises */}
      {stats.workouts?.top_exercises?.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Your Top Exercises</h3>
          <div className="top-exercises">
            {stats.workouts.top_exercises.map((ex, i) => (
              <div key={i} className="exercise-bar-item">
                <div className="exercise-bar-label">
                  <span className="exercise-rank">#{i + 1}</span>
                  {ex.name}
                </div>
                <div className="exercise-bar-wrap">
                  <div
                    className="exercise-bar"
                    style={{
                      width: `${(ex.times_performed / stats.workouts.top_exercises[0].times_performed) * 100}%`
                    }}
                  />
                </div>
                <span className="exercise-count">{ex.times_performed}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
